import type {
  ConsentState,
  ComplianceRule,
  BannerConfig,
  BannerTexts,
  CookiedConfig,
  ComplianceResponse,
} from './types';
import { createStyles } from './styles';
import { createBannerHTML, createPreferencesHTML } from './templates';

const STORAGE_KEY = 'cookied_consent';
const VIEW_TRACKED_KEY = 'cookied_view_tracked';

// Injected by rollup (see rollup.config.js).
declare const __COOKIED_API_ENDPOINT__: string;
declare const __COOKIED_INGEST_ENDPOINT__: string;
declare const __COOKIED_ANON_KEY__: string;

// Config reads (GET /compliance). This stays pointed at the Vercel proxy on
// purpose: the Edge Function behind it runs five database queries per call, and
// this is the highest-volume endpoint in the product, so it wants the shared CDN
// cache sitting in front of it. Sending it straight to Supabase would turn cheap
// cache hits into full function invocations.
const DEFAULT_API_ENDPOINT = __COOKIED_API_ENDPOINT__;

// Writes (POST /consent, POST /banner-view). These can never be cached, so a
// proxy in front of them buys nothing at all - it just adds a Vercel function
// invocation (plus its CORS preflight) to every write. Point them straight at
// Supabase. Falls back to the read endpoint when the build does not set it, so
// an un-configured build behaves exactly as it does today.
const DEFAULT_INGEST_ENDPOINT = __COOKIED_INGEST_ENDPOINT__ || __COOKIED_API_ENDPOINT__;

// Only set when the ingest endpoint is Supabase directly. It satisfies the Edge
// Function gateway; the real authorization is the per-site `ck_` API key, which
// every function validates against the `websites` table. The anon key is public
// by design, so shipping it in a browser bundle is expected.
const API_ANON_KEY = __COOKIED_ANON_KEY__;

/**
 * Auth headers for the direct-to-Supabase ingest endpoint.
 *
 * Deliberately NOT applied to the compliance read. That request is a plain GET
 * today, which makes it a CORS-simple request with no preflight; adding an
 * Authorization header to it would buy a preflight invocation on the busiest
 * endpoint we have. The proxy attaches its own credentials server-side anyway.
 */
function ingestAuthHeaders(): Record<string, string> {
  return API_ANON_KEY
    ? { apikey: API_ANON_KEY, Authorization: `Bearer ${API_ANON_KEY}` }
    : {};
}

const DEFAULT_TEXTS: BannerTexts = {
  title: 'Cookie Consent',
  description: 'We use cookies to enhance your experience.',
  acceptAll: 'Accept All',
  rejectAll: 'Reject All',
  managePreferences: 'Preferences',
  savePreferences: 'Save',
  privacyPolicyLink: '/privacy',
  categories: {
    necessary: { title: 'Necessary', description: 'Required for the website to function.' },
    analytics: { title: 'Analytics', description: 'Help us understand usage.' },
    marketing: { title: 'Marketing', description: 'Personalized advertisements.' },
  },
};

export class CookiedBanner {
  private apiKey: string = '';
  private config: CookiedConfig = {};
  private compliance: ComplianceRule | null = null;
  private bannerConfig: BannerConfig | null = null;
  private consent: ConsentState | null = null;
  private bannerElement: HTMLElement | null = null;
  private preferencesElement: HTMLElement | null = null;
  private styleElement: HTMLStyleElement | null = null;
  private initialized: boolean = false;
  private viewTracked: boolean = false;
  private blockedScripts: Map<HTMLScriptElement, { src: string; category: string }> = new Map();

  /**
   * Initialize the cookie banner
   */
  async init(apiKey: string, config: Partial<CookiedConfig> = {}): Promise<void> {
    if (this.initialized) {
      console.warn('[Cookied] Already initialized');
      return;
    }

    this.apiKey = apiKey;
    this.config = {
      apiEndpoint: DEFAULT_API_ENDPOINT,
      ingestEndpoint: DEFAULT_INGEST_ENDPOINT,
      language: navigator.language.split('-')[0] || 'en',
      ...config,
    };

    // Someone pointing the banner somewhere else (local dev, self-hosting) sets
    // apiEndpoint alone and expects everything to follow it. Only fall back to
    // the built-in split when they have not redirected the reads themselves.
    if (config.apiEndpoint && !config.ingestEndpoint) {
      this.config.ingestEndpoint = config.apiEndpoint;
    }

    try {
      // Block scripts immediately before anything else
      this.blockScripts();

      // Load stored consent
      this.consent = this.getStoredConsent();

      // Fetch compliance rules and config
      await this.fetchComplianceConfig();

      if (!this.compliance || !this.bannerConfig) {
        throw new Error('Failed to load configuration');
      }

      // Check if we need to show the banner
      if (this.shouldShowBanner()) {
        this.showBanner();
      } else if (this.consent) {
        // Apply stored consent
        this.applyConsent(this.consent);
      }

      this.initialized = true;
    } catch (error) {
      console.error('[Cookied] Initialization failed:', error);
      // On error, unblock all scripts to not break the website
      this.unblockAllScripts();
    }
  }

  /**
   * Fetch compliance rules and banner config from API
   */
  private async fetchComplianceConfig(): Promise<void> {
    const country = this.detectCountry();
    const endpoint = `${this.config.apiEndpoint}/compliance`;
    const url = `${endpoint}?country=${country}&apiKey=${this.apiKey}`;

    // Deliberately sent with no headers at all. A GET has no body, so
    // Content-Type described nothing - but it made this a non-simple CORS
    // request, so every config read was preceded by its own OPTIONS preflight.
    // Preflights are never served from the CDN cache, so on the busiest endpoint
    // in the product that header was doubling origin invocations for free.
    const response = await fetch(url, { method: 'GET' });

    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }

    const data: ComplianceResponse = await response.json();
    this.compliance = data.rules;
    this.bannerConfig = data.config;
  }

  /**
   * Detect visitor's country from Cloudflare header or fallback
   */
  private detectCountry(): string {
    // Try to get from Cloudflare header (set by edge)
    const cfCountry = document.querySelector('meta[name="cf-ipcountry"]')?.getAttribute('content');
    if (cfCountry) return cfCountry;

    // Try to get from request header (if available in server-side rendering context)
    // This won't work in client-side, but included for completeness
    if (typeof window !== 'undefined' && (window as any).__COOKIED_COUNTRY__) {
      return (window as any).__COOKIED_COUNTRY__;
    }

    // Fallback to timezone-based detection
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Expanded timezone to country mapping
    const tzMap: Record<string, string> = {
      // Europe
      'Europe/London': 'GB',
      'Europe/Paris': 'FR',
      'Europe/Berlin': 'DE',
      'Europe/Amsterdam': 'NL',
      'Europe/Brussels': 'BE',
      'Europe/Rome': 'IT',
      'Europe/Madrid': 'ES',
      'Europe/Athens': 'GR',
      'Europe/Lisbon': 'PT',
      'Europe/Dublin': 'IE',
      'Europe/Vienna': 'AT',
      'Europe/Stockholm': 'SE',
      'Europe/Copenhagen': 'DK',
      'Europe/Helsinki': 'FI',
      'Europe/Warsaw': 'PL',
      'Europe/Prague': 'CZ',
      'Europe/Budapest': 'HU',
      'Europe/Bucharest': 'RO',
      'Europe/Sofia': 'BG',
      'Europe/Zagreb': 'HR',
      'Europe/Zurich': 'CH',
      'Europe/Oslo': 'NO',
      'Europe/Reykjavik': 'IS',

      // Americas
      'America/New_York': 'US',
      'America/Chicago': 'US',
      'America/Denver': 'US',
      'America/Los_Angeles': 'US',
      'America/Phoenix': 'US',
      'America/Anchorage': 'US',
      'America/Honolulu': 'US',
      'America/Toronto': 'CA',
      'America/Vancouver': 'CA',
      'America/Montreal': 'CA',
      'America/Mexico_City': 'MX',
      'America/Sao_Paulo': 'BR',
      'America/Buenos_Aires': 'AR',
      'America/Santiago': 'CL',
      'America/Bogota': 'CO',
      'America/Lima': 'PE',

      // Asia
      'Asia/Tokyo': 'JP',
      'Asia/Shanghai': 'CN',
      'Asia/Hong_Kong': 'HK',
      'Asia/Singapore': 'SG',
      'Asia/Seoul': 'KR',
      'Asia/Bangkok': 'TH',
      'Asia/Jakarta': 'ID',
      'Asia/Manila': 'PH',
      'Asia/Kolkata': 'IN',
      'Asia/Dubai': 'AE',
      'Asia/Riyadh': 'SA',
      'Asia/Tel_Aviv': 'IL',
      'Asia/Istanbul': 'TR',

      // Oceania
      'Australia/Sydney': 'AU',
      'Australia/Melbourne': 'AU',
      'Australia/Brisbane': 'AU',
      'Australia/Perth': 'AU',
      'Pacific/Auckland': 'NZ',

      // Africa
      'Africa/Johannesburg': 'ZA',
      'Africa/Cairo': 'EG',
      'Africa/Lagos': 'NG',
      'Africa/Nairobi': 'KE',
    };

    // Try exact match first
    if (tzMap[timezone]) {
      return tzMap[timezone];
    }

    // Try partial match (e.g., "Europe/" prefix)
    for (const [tz, country] of Object.entries(tzMap)) {
      if (timezone.startsWith(tz.split('/')[0] + '/')) {
        // If timezone starts with same continent, use a reasonable default
        if (tz.startsWith('Europe/')) return 'GB'; // Default to UK for Europe
        if (tz.startsWith('America/')) return 'US'; // Default to US for Americas
        if (tz.startsWith('Asia/')) return 'JP'; // Default to Japan for Asia
        if (tz.startsWith('Australia/') || tz.startsWith('Pacific/')) return 'AU';
        if (tz.startsWith('Africa/')) return 'ZA'; // Default to South Africa for Africa
      }
    }

    // Final fallback: try to infer from timezone name
    if (timezone.includes('Europe')) return 'GB';
    if (timezone.includes('America')) return 'US';
    if (timezone.includes('Asia')) return 'JP';
    if (timezone.includes('Australia') || timezone.includes('Pacific')) return 'AU';
    if (timezone.includes('Africa')) return 'ZA';

    // Ultimate fallback
    return 'US';
  }

  /**
   * Block all scripts with cookied category attribute
   */
  private blockScripts(): void {
    const scripts = document.querySelectorAll('script[data-cookied-category]');

    scripts.forEach((script) => {
      const element = script as HTMLScriptElement;
      const category = element.getAttribute('data-cookied-category') || '';
      const src = element.getAttribute('data-cookied-src') || element.src;

      if (category !== 'necessary') {
        // Store original info
        this.blockedScripts.set(element, { src, category });

        // Disable the script
        element.type = 'text/plain';
        element.removeAttribute('src');
      }
    });
  }

  /**
   * Activate scripts for given categories
   */
  private activateScripts(categories: string[]): void {
    this.blockedScripts.forEach((info, script) => {
      if (categories.includes(info.category)) {
        // Create a new script element to execute
        const newScript = document.createElement('script');
        newScript.src = info.src;
        newScript.async = true;

        // Copy other attributes
        Array.from(script.attributes).forEach((attr) => {
          if (!['type', 'src', 'data-cookied-category', 'data-cookied-src'].includes(attr.name)) {
            newScript.setAttribute(attr.name, attr.value);
          }
        });

        // Replace old script with new one
        script.parentNode?.replaceChild(newScript, script);
        this.blockedScripts.delete(script);
      }
    });
  }

  /**
   * Unblock all scripts (fallback on error)
   */
  private unblockAllScripts(): void {
    this.activateScripts(['analytics', 'marketing']);
  }

  /**
   * Check if banner should be shown
   */
  private shouldShowBanner(): boolean {
    if (!this.compliance) return true;
    if (!this.consent) return true;

    // Check expiry
    if (this.consent.timestamp) {
      const consentDate = new Date(this.consent.timestamp);
      const now = new Date();
      const daysDiff = (now.getTime() - consentDate.getTime()) / (1000 * 60 * 60 * 24);

      if (daysDiff > this.compliance.consentExpiryDays) {
        return true;
      }
    }

    return false;
  }

  /**
   * Get stored consent from localStorage
   */
  private getStoredConsent(): ConsentState | null {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return null;
      return JSON.parse(stored);
    } catch {
      return null;
    }
  }

  /**
   * Save consent to localStorage
   */
  private saveConsent(consent: ConsentState): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(consent));
    } catch {
      console.warn('[Cookied] Failed to save consent');
    }
  }

  /**
   * Get current banner texts for the configured language
   */
  private getTexts(): BannerTexts {
    if (!this.bannerConfig) {
      return DEFAULT_TEXTS;
    }

    const lang = this.config.language || 'en';
    const texts = this.bannerConfig.texts[lang] || this.bannerConfig.texts['en'];

    if (!texts) {
      return DEFAULT_TEXTS;
    }

    return texts;
  }

  /**
   * Show the cookie banner
   */
  showBanner(): void {
    if (!this.compliance || !this.bannerConfig) return;
    if (this.bannerElement) return; // Already showing

    // Inject styles
    this.injectStyles();

    // Create banner element
    const texts = this.getTexts();
    const html = createBannerHTML(
      texts,
      this.bannerConfig.theme,
      this.compliance.requireRejectButton,
      this.compliance.requirePreferencesButton,
      this.bannerConfig.showBranding
    );

    this.bannerElement = document.createElement('div');
    this.bannerElement.id = 'cookied-banner';
    this.bannerElement.innerHTML = html;
    document.body.appendChild(this.bannerElement);

    // Bind events
    this.bindBannerEvents();

    // Announce for screen readers
    this.bannerElement.setAttribute('role', 'dialog');
    this.bannerElement.setAttribute('aria-label', texts.title);
    this.bannerElement.setAttribute('aria-modal', 'false');

    // Track banner view (async, non-blocking)
    this.trackBannerView();

    // Callback
    this.config.onBannerShow?.();
  }

  /**
   * Track banner view for analytics
   * Only tracks once per session to avoid duplicate counts
   */
  private async trackBannerView(): Promise<void> {
    // Check if already tracked in this session
    if (this.viewTracked) return;

    try {
      // Check sessionStorage to avoid tracking same session
      const sessionTracked = sessionStorage.getItem(VIEW_TRACKED_KEY);
      if (sessionTracked === this.apiKey) {
        this.viewTracked = true;
        return;
      }
    } catch {
      // sessionStorage might not be available
    }

    this.viewTracked = true;

    try {
      // Mark as tracked in session
      sessionStorage.setItem(VIEW_TRACKED_KEY, this.apiKey);
    } catch {
      // sessionStorage might not be available
    }

    // Send view tracking (fire-and-forget)
    try {
      const endpoint = `${this.config.ingestEndpoint}/banner-view`;
      await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...ingestAuthHeaders(),
        },
        // userAgent and country used to reach the Edge Function as the
        // x-user-agent / cf-ipcountry headers that the Vercel proxy synthesised
        // on our behalf. Calling Supabase directly there is no proxy to do that,
        // and a browser cannot set those headers itself, so send them in the
        // body - the function already prefers the header and falls back to
        // these fields (see supabase/functions/banner-view/index.ts).
        body: JSON.stringify({
          apiKey: this.apiKey,
          visitorId: this.generateVisitorId(),
          region: this.compliance?.regionCode || 'ROW',
          country: this.detectCountry(),
          pageUrl: typeof window !== 'undefined' ? window.location.href : undefined,
          referrer: typeof document !== 'undefined' ? document.referrer : undefined,
          userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
        }),
      });
    } catch {
      // Silently fail - view tracking is non-critical
    }
  }

  /**
   * Hide the banner
   */
  hideBanner(): void {
    if (this.bannerElement) {
      this.bannerElement.classList.add('cookied-hide');
      setTimeout(() => {
        this.bannerElement?.remove();
        this.bannerElement = null;
      }, 300);
    }

    if (this.preferencesElement) {
      this.preferencesElement.remove();
      this.preferencesElement = null;
    }

    this.config.onBannerHide?.();
  }

  /**
   * Inject CSS styles
   */
  private injectStyles(): void {
    if (this.styleElement) return;

    this.styleElement = document.createElement('style');
    this.styleElement.id = 'cookied-styles';
    this.styleElement.textContent = createStyles(this.bannerConfig?.theme);

    // Add custom CSS if provided
    if (this.bannerConfig?.customCss) {
      this.styleElement.textContent += '\n' + this.bannerConfig.customCss;
    }

    document.head.appendChild(this.styleElement);
  }

  /**
   * Bind event handlers to banner buttons
   */
  private bindBannerEvents(): void {
    // Accept all
    this.bannerElement?.querySelector('#cookied-accept-all')?.addEventListener('click', () => {
      this.acceptAll();
    });

    // Reject all
    this.bannerElement?.querySelector('#cookied-reject-all')?.addEventListener('click', () => {
      this.rejectAll();
    });

    // Manage preferences
    this.bannerElement?.querySelector('#cookied-preferences')?.addEventListener('click', () => {
      this.showPreferences();
    });
  }

  /**
   * Show preferences modal
   */
  showPreferences(): void {
    if (this.preferencesElement) return;

    const texts = this.getTexts();
    const currentConsent = this.consent || this.compliance?.defaultConsent || {
      necessary: true,
      analytics: false,
      marketing: false,
    };

    const html = createPreferencesHTML(
      texts,
      this.bannerConfig?.theme,
      currentConsent,
      this.compliance?.allowPreChecked || false
    );

    this.preferencesElement = document.createElement('div');
    this.preferencesElement.id = 'cookied-preferences-modal';
    this.preferencesElement.innerHTML = html;
    document.body.appendChild(this.preferencesElement);

    // Add body class to manage stacking contexts
    document.body.classList.add('cookied-modal-open');

    // Bind events
    this.preferencesElement.querySelector('#cookied-save-preferences')?.addEventListener('click', () => {
      this.savePreferences();
    });

    this.preferencesElement.querySelector('#cookied-close-preferences')?.addEventListener('click', () => {
      this.closePreferences();
    });

    // Focus trap for accessibility
    const firstFocusable = this.preferencesElement.querySelector('button, input') as HTMLElement;
    firstFocusable?.focus();
  }

  /**
   * Close preferences modal
   */
  closePreferences(): void {
    if (this.preferencesElement) {
      this.preferencesElement.classList.add('cookied-hide');
      // Remove body class to restore page stacking contexts
      document.body.classList.remove('cookied-modal-open');
      setTimeout(() => {
        this.preferencesElement?.remove();
        this.preferencesElement = null;
      }, 300);
    }
  }

  /**
   * Accept all cookies
   */
  acceptAll(): void {
    const consent: ConsentState = {
      necessary: true,
      analytics: true,
      marketing: true,
      timestamp: new Date().toISOString(),
      version: '1.0',
    };

    this.applyConsent(consent);
    this.saveConsent(consent);
    this.hideBanner();
    this.sendConsentToServer(consent);
  }

  /**
   * Reject all non-essential cookies
   */
  rejectAll(): void {
    const consent: ConsentState = {
      necessary: true,
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString(),
      version: '1.0',
    };

    this.applyConsent(consent);
    this.saveConsent(consent);
    this.hideBanner();
    this.sendConsentToServer(consent);
  }

  /**
   * Save preferences from modal
   */
  savePreferences(): void {
    const analyticsCheckbox = this.preferencesElement?.querySelector('#cookied-analytics') as HTMLInputElement;
    const marketingCheckbox = this.preferencesElement?.querySelector('#cookied-marketing') as HTMLInputElement;

    const consent: ConsentState = {
      necessary: true,
      analytics: analyticsCheckbox?.checked || false,
      marketing: marketingCheckbox?.checked || false,
      timestamp: new Date().toISOString(),
      version: '1.0',
    };

    this.applyConsent(consent);
    this.saveConsent(consent);
    this.closePreferences();
    this.hideBanner();
    this.sendConsentToServer(consent);
  }

  /**
   * Apply consent - activate/deactivate scripts
   */
  private applyConsent(consent: ConsentState): void {
    this.consent = consent;

    const categories: string[] = ['necessary'];
    if (consent.analytics) categories.push('analytics');
    if (consent.marketing) categories.push('marketing');

    this.activateScripts(categories);

    // Dispatch custom event for integrations
    window.dispatchEvent(new CustomEvent('cookied:consent', {
      detail: consent,
    }));

    // Callback
    this.config.onConsentChange?.(consent);
  }

  /**
   * Send consent to server for audit purposes
   */
  private async sendConsentToServer(consent: ConsentState): Promise<void> {
    try {
      const endpoint = `${this.config.ingestEndpoint}/consent`;
      await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...ingestAuthHeaders(),
        },
        body: JSON.stringify({
          apiKey: this.apiKey,
          visitorId: this.generateVisitorId(),
          consent: {
            necessary: consent.necessary,
            analytics: consent.analytics,
            marketing: consent.marketing,
          },
          region: this.compliance?.regionCode || 'ROW',
        }),
      });
    } catch (error) {
      // Silently fail - consent is already stored client-side
      // Log error in development
      if (process.env.NODE_ENV === 'development') {
        console.warn('[Cookied] Failed to send consent to server:', error);
      }
    }
  }

  /**
   * Generate anonymous visitor ID
   */
  private generateVisitorId(): string {
    const components = [
      navigator.userAgent,
      navigator.language,
      new Date().getTimezoneOffset().toString(),
      screen.width.toString(),
      screen.height.toString(),
    ];

    let hash = 0;
    const str = components.join('|');
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }

    return `ck_${Math.abs(hash).toString(36)}`;
  }

  // Public API

  /**
   * Get current consent state
   */
  getConsent(): ConsentState | null {
    return this.consent;
  }

  /**
   * Check if a category is consented
   */
  hasConsent(category: 'analytics' | 'marketing'): boolean {
    return this.consent?.[category] || false;
  }

  /**
   * Revoke consent and show banner again
   */
  revokeConsent(): void {
    localStorage.removeItem(STORAGE_KEY);
    this.consent = null;
    this.showBanner();
  }

  /**
   * Update consent programmatically
   */
  updateConsent(consent: Partial<ConsentState>): void {
    const newConsent: ConsentState = {
      necessary: true,
      analytics: consent.analytics ?? this.consent?.analytics ?? false,
      marketing: consent.marketing ?? this.consent?.marketing ?? false,
      timestamp: new Date().toISOString(),
      version: '1.0',
    };

    this.applyConsent(newConsent);
    this.saveConsent(newConsent);
    this.sendConsentToServer(newConsent);
  }
}

